/**
 * Much of the complexity of selection and multi-selection happens because there are many
 * mouse and selection handlers - custom groups have custom selection logic, React Flow's
 * onNodesChange also handles selection, and the multi-select overlay has its own selection logic.
 */
import { useCallback, useEffect } from 'react';
import { useFlowView } from '../FlowContext';
import type { NodeId } from 'web';

const MULTI_SELECT_KEY = 'Shift' as const;
// CSS hook consumed by body.shift-held selectors in UI/css/index.css — keep the two in sync.
const MULTI_SELECT_KEY_HELD_CLASS = 'shift-held';

type ModifierAwareEvent = React.MouseEvent | MouseEvent | React.KeyboardEvent | KeyboardEvent;

export const isMultiSelectEvent = (event: ModifierAwareEvent): boolean => event.getModifierState(MULTI_SELECT_KEY);

export const consumeMultiSelectEvent = (event: ModifierAwareEvent): boolean => {
  if (!isMultiSelectEvent(event)) return false;
  event.stopPropagation();
  return true;
};

const setMultiSelectKeyHeldClass = (held: boolean) => {
  if (held) {
    document.body.classList.add(MULTI_SELECT_KEY_HELD_CLASS);
  } else {
    document.body.classList.remove(MULTI_SELECT_KEY_HELD_CLASS);
  }
};

/**
 * While the multi-select modifier is held, toggle a body class that CSS uses to disable
 * pointer events on the multi-select overlay. This lets modifier-clicks fall through to
 * the node beneath, where selection logic can run. We do this instead of lowering the
 * overlay's z-index so that drag-to-reposition still works.
 *
 * Call once at the flow root. Consumers of `useSelectionActions` rely on this but do not
 * install the listeners themselves.
 */
export const useAllowOverlayPassthrough = () => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === MULTI_SELECT_KEY) setMultiSelectKeyHeldClass(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === MULTI_SELECT_KEY) setMultiSelectKeyHeldClass(false);
    };
    // Release on focus loss (Cmd+Tab, window switches) so the class can't get stuck.
    const onBlur = () => setMultiSelectKeyHeldClass(false);
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') setMultiSelectKeyHeldClass(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      setMultiSelectKeyHeldClass(false);
    };
  }, []);
};

export const useSelectionActions = () => {
  const flowView = useFlowView();

  // The multi-select overlay flag (`nodesSelectionActive`) is kept in sync via the
  // `useOnSelectionChange` subscription in Flow.jsx, so this just owns the selection itself.
  const replaceSelection = useCallback(
    (nodeIds: NodeId[]) => {
      flowView.selectedNodes = nodeIds;
    },
    [flowView],
  );

  const toggleNodeInSelection = useCallback(
    (nodeId: NodeId) => {
      const current = flowView.selectedNodes;
      const next = current.includes(nodeId) ? current.filter((n) => n !== nodeId) : [...current, nodeId];
      replaceSelection(next);
    },
    [flowView, replaceSelection],
  );

  return { replaceSelection, toggleNodeInSelection };
};
